import { useMemo } from 'react';
import { useFetchQueryParams } from 'assets/js/pages/reports/useFetchQueryParams';
import { transformPartitionedSavedFilters } from 'components/filter/filters-next/downstream';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { FilterGroup } from 'components/filter/types';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { isoDateFromServer } from 'helpers/dates';
import { isEqual, omit } from 'lodash';
import { DateTime } from 'luxon';
import { ReportDateRange } from 'types/domain';

const useQueryDiff = () => {
    const { data: report } = useCurrentReport();
    const params = useFetchQueryParams();
    const { filterGroups } = useFiltersByKey('singleReport');

    const hasFilterDiff = useMemo(
        () =>
            !isEqual(
                omitIdsFromFilterGroups(
                    transformPartitionedSavedFilters(report.query.filters ?? []),
                ),
                omitIdsFromFilterGroups(filterGroups),
            ),
        [filterGroups, report.query.filters],
    );

    const query = params
        ? {
              columns: params.columns,
              graph_settings: params.graph_settings,
              display_settings: params.display_settings,
              date_range: normalizeRanges(params.date_range),
          }
        : undefined;

    const reportQuery = {
        columns: report.query.columns,
        graph_settings: report.query.graph_settings,
        display_settings: report.query.display_settings,
        date_range: normalizeRanges(report.query.date_range),
    };

    const hasQueryParamDiff = !!query && !isEqual(reportQuery, query);

    return hasFilterDiff || hasQueryParamDiff;
};

interface WithId {
    id: string;
}

const normalizeRanges = (range: ReportDateRange) => ({
    ...range,
    range: range.range.map(r => (r ? isoDateFromServer(r).toLocaleString(DateTime.DATE_MED) : '')),
});

const omitIdFromFilter = <T extends WithId>(value: T) => omit(value, 'id');
const omitIdsFromFilterGroups = (groups: FilterGroup[]) =>
    groups.map(f =>
        omitIdFromFilter({
            ...f,
            filters: f.filters.map(omitIdFromFilter),
        }),
    );

export { useQueryDiff };
